import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import './certificate-donation.scss';
import { LANGUAGE_OPTIONS, Regex_Emoji } from '../../../../../utils/Utils';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnAlignType } from 'primereact/column';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import DonorManagerAPI from '../../../../../services/donorAPI';
import { generateFileDownload, parseFloatByLanguage, showNotification } from '../../../../../utils/functionCommon';
import { withTranslation } from 'react-i18next';

const CertificateDonation = (props: any) => {
  const { formatDate, donationCertificate, isLoading, getDonationDetails, donation, token, id, toast, isSubscriptionDonation, t, i18n } = props;

  const formikRef = useRef(null);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [isEditingCertificate, setIsEditingCertificate] = useState(false);

  const onHandleDownloadCertificate = async (donation: any, year: any, isParent: any) => {
    try {
      setIsLoadingAction(true);
      // @ts-ignore: Object is possibly 'null'.
      const { values } = formikRef?.current;
      let lang = values.payment_language_code === 'de' ? 'DE' : 'EN';
      const pdfResponse =
        isSubscriptionDonation(donation) && !isParent
          ? await DonorManagerAPI.getRecurringCertificateByYear(donation.id, values.payment_language_code, year, token || '')
          : await DonorManagerAPI.getCertificate(donation.id, values.payment_language_code, token || '');
      if (pdfResponse && pdfResponse.status === 200) {
        const prefixCertificateName = lang === 'DE' ? 'Urkunde' : 'Certificate';
        let nameCertificate =
          isSubscriptionDonation(donation) && !isParent ? `${prefixCertificateName}_${donation.serial}_${+year + 1}_${lang}` : `${prefixCertificateName}_${donation.serial}_${lang}`;
        generateFileDownload(nameCertificate, pdfResponse.data.content);
        setIsLoadingAction(false);
        showNotification('success', t('download_certificate_success'), toast);
      }
    } catch (error) {
      setIsLoadingAction(false);
      showNotification('error', t('download_certificate_error'), toast);
    }
  };

  const onHandleUpdateCertificateInfo = async () => {
    try {
      // @ts-ignore: Object is possibly 'null'.
      const { values, dirty, isValid } = formikRef?.current;
      // @ts-ignore: Object is possibly 'null'.
      formikRef.current.validateForm();
      // @ts-ignore: Object is possibly 'null'.
      if (formikRef && dirty && isValid) {
        let dataUpdate = {
          payment_language_code: values.payment_language_code,
          personalize_certificate_reason: values?.personalize_certificate_reason?.trim(),
          personalize_certificate_display_name: values?.personalize_certificate_display_name?.trim(),
        };

        const resUpdate = await DonorManagerAPI.updateDonorDonation(id || '', { ...dataUpdate }, token || '');
        if (resUpdate && resUpdate.status === 200) {
          getDonationDetails(id);
          showNotification('success', t('save_certificate_success'), toast);
        }
      }
    } catch (error) {
      showNotification('error', t('save_certificate_error'), toast);
    }
  };

  const initialFormValues = () => {
    let certificateDisplayName =
      donation?.personalize_certificate_display_name ||
      donation?.payment_company_name ||
      `${donation?.payment_first_name} ${donation?.payment_last_name}` ||
      '';

    let initialCertificate = {
      payment_language_code: donation ? donation?.payment_language_code : 'en',
      personalize_certificate_display_name: certificateDisplayName || '',
      personalize_certificate_reason: donation?.personalize_certificate_reason || '',
    };

    return initialCertificate;
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        personalize_certificate_display_name: Yup.string().nullable().required(t('field_validate')),
      }),
    [i18n.language],
  );

  useEffect(() => {
    // @ts-ignore: Object is possibly 'null'.
    formikRef.current?.validateForm();
  }, [i18n.language]);

  const tableColumnsCertificate = [
    {
      field: 'payment_date',
      header: (a: any) => {
        return t('certificate_table_header_0');
      },
      width: '30%',
      align: 'left',
      bodyTemPlate: (rowData: any) => {
        return formatDate(rowData.payment_date);
      },
    },
    {
      field: 'total_amount',
      header: (a: any) => {
        return t('certificate_table_header_1');
      },
      width: '60%',
      align: 'left',
      bodyTemPlate: (rowData: any) => {
        return (
          <div>
            {rowData ? parseFloatByLanguage(rowData.area, t('language')) : '-'} <span>m²</span>
          </div>
        );
      },
    },
    {
      field: '',
      header: '',
      width: '10%',
      alignFrozen: 'right',
      align: 'right',
      bodyTemPlate: (rowData: any) => {
        return (
          <span
            className={`${isEditingCertificate ? 'download-btn-disabled' : 'download-btn'}`}
            onClick={() => {
              onHandleDownloadCertificate(donation, rowData.year, rowData.isParent);
            }}
          >
            <i className="fa-solid fa-download"></i>
          </span>
        );
      },
    },
  ];

  const dynamicColumnsCertificate = tableColumnsCertificate.map((col, i) => {
    return (
      <Column
        key={col.field}
        field={col.field}
        header={col.header}
        style={{
          width: col.width,
        }}
        body={col.bodyTemPlate}
        align={col.align as ColumnAlignType}
      />
    );
  });

  return (
    <div className="certificate-donation">
      {isLoadingAction && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-icon"></div>
          </div>
        </div>
      )}
      <div className="certificate-donation-container">
        <div className="donation-details-body bg-gray bg-linear">
          <div className="aw_container donation-details-body-container">
            <div className="donation-certificate">
              <div className="donation-certificate-container">
                <div className="donation-receipt-container row">
                  <div className="donation-certificate-section col-md-6 col-12">
                    <div className="donation-certificate-field">
                      <div className="donation-certificate-content-title">{t('personalize_ceriticate')}</div>
                    </div>
                    <Formik
                      innerRef={formikRef}
                      initialValues={initialFormValues()}
                      validationSchema={validationSchema}
                      onSubmit={(values, { setSubmitting }) => {
                        // console.log("values >>>", values);
                      }}
                    >
                      {({ values, errors, touched, dirty, isValid, handleBlur, handleSubmit, setFieldValue, handleChange, resetForm }) => (
                        <form onSubmit={handleSubmit}>
                          <div className="row-content">
                            <div className="row">
                              <div className="col-md-12">
                                <div className="element-form">
                                  <SelectButton
                                    name="payment_language_code"
                                    id="payment_language_code"
                                    disabled={!isEditingCertificate}
                                    className={`donation-checkbox ${!isEditingCertificate && 'diabled-payment-options'}`}
                                    options={LANGUAGE_OPTIONS}
                                    optionLabel={`name.${t('language')}`}
                                    optionValue="code"
                                    onChange={(item: any) => {
                                      if (item.value) {
                                        setFieldValue('payment_language_code', item.value, true);
                                      }
                                    }}
                                    value={values.payment_language_code}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-12">
                                <div className="input-item">
                                  <span className="p-float-label">
                                    <InputText
                                      className={`${touched.personalize_certificate_display_name && errors.personalize_certificate_display_name ? 'has-error' : ''}`}
                                      name="personalize_certificate_display_name"
                                      key={isEditingCertificate ? 'enabled' : 'disabled'}
                                      onChange={(item: any) => {
                                        const text = item.target.value;
                                        const sanitizedText = text.replace(Regex_Emoji, '');
                                        if (sanitizedText.length <= 80) {
                                          setFieldValue('personalize_certificate_display_name', sanitizedText, true);
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      value={values.personalize_certificate_display_name}
                                      maxLength={80}
                                      disabled={!isEditingCertificate}
                                      id="personalize_certificate_display_name"
                                    />
                                    <label className="label" htmlFor="personalize_certificate_display_name">
                                      {t('name_on_certificate')} <span>*</span>
                                    </label>
                                  </span>
                                  <div
                                    className={`${touched.personalize_certificate_display_name && errors.personalize_certificate_display_name ? 'error' : ''}`}
                                  >
                                    {touched.personalize_certificate_display_name && errors.personalize_certificate_display_name
                                      ? `${errors.personalize_certificate_display_name}`
                                      : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-12">
                                <div className="input-item">
                                  <span className="p-float-label">
                                    <InputText
                                      className={``}
                                      name="personalize_certificate_reason"
                                      key={isEditingCertificate ? 'enabled' : 'disabled'}
                                      onChange={(item: any) => {
                                        const text = item.target.value;
                                        const sanitizedText = text.replace(Regex_Emoji, '');
                                        if (sanitizedText.length <= 80) {
                                          setFieldValue('personalize_certificate_reason', sanitizedText, true);
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      value={values.personalize_certificate_reason}
                                      maxLength={80}
                                      disabled={!isEditingCertificate}
                                      id="personalize_certificate_reason"
                                    />
                                    <label className="label" htmlFor="personalize_certificate_reason">
                                      {t('occasion')}
                                    </label>
                                  </span>
                                  <div
                                    className={`${touched.personalize_certificate_reason && errors.personalize_certificate_reason ? 'error' : ''}`}
                                  >
                                    {touched.personalize_certificate_reason && errors.personalize_certificate_reason
                                      ? `${errors.personalize_certificate_reason}`
                                      : ''}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="donation-certificate-field donation-certificate-btn">
                              <div className="btn-wrapper">
                                <Button
                                  type="submit"
                                  className="p-button-rounded wi-main-button wi-icon-edit"
                                  icon="fa-solid fa-pencil"
                                  iconPos="right"
                                  hidden={isEditingCertificate}
                                  onClick={() => setIsEditingCertificate(true)}
                                />
                                <Button
                                  type="submit"
                                  className="p-button-rounded wi-main-button wi-icon-edit wi-icon-cancel"
                                  icon="fa-solid fa-chevron-left"
                                  iconPos="right"
                                  hidden={!isEditingCertificate}
                                  onClick={() => {
                                    resetForm();
                                    setIsEditingCertificate(false);
                                  }}
                                />
                                <Button
                                  type="submit"
                                  label={t('save')}
                                  className="p-button-rounded wi-main-button donation-receipt-download-button wi-btn-save"
                                  icon="fa-solid fa-chevron-right"
                                  iconPos="right"
                                  hidden={!isEditingCertificate}
                                  disabled={!(isValid && dirty)}
                                  onClick={() => onHandleUpdateCertificateInfo()}
                                />
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </Formik>
                  </div>
                  <div className="donation-certificate-section col-md-6 col-12 donation-certificate-section-white-mobile">
                    <div className="table-detail-container">
                      <div className="donation-certificate-content-title">{t('certificates')}</div>
                      <div className="table-children">
                        <DataTable
                          loading={isLoading}
                          value={donationCertificate}
                          scrollable
                          scrollHeight="calc(100vh - 166px)"
                          scrollDirection="both"
                          responsiveLayout="scroll"
                        >
                          {dynamicColumnsCertificate}
                        </DataTable>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation('language')(memo(CertificateDonation));
